// useIntlAccaTournamentAdmin — founder-only tournament lifecycle
// control (PRD §5 Tournament Calendar Management).
//
// `isAdmin` is resolved from the is_strikza_admin() RPC (same trust
// model as components/admin/AdminOnly.tsx — the founder uid lives
// ONLY in the SQL function, never client-side). `advance()` calls
// public.intl_acca_set_tournament_state(), which itself re-checks
// is_strikza_admin() server-side and enforces the forward-only state
// machine — so this hook is UX convenience, not the security boundary.

import { useCallback, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useRpcQuery } from "../data/useRpcQuery.ts";
import type { IntlAccaTournament } from "../db/intl-acca-types";

type Status = IntlAccaTournament["status"];

/** Forward-only transition map — mirrors the RPC's allowed steps. */
export const NEXT_STATE: Partial<Record<Status, Status>> = {
  PRE_TOURNAMENT: "ACTIVE",
  ACTIVE: "POST_TOURNAMENT",
  POST_TOURNAMENT: "ARCHIVED",
  // ARCHIVED is terminal — no entry.
};

export interface IntlAccaTournamentAdminVM {
  isAdmin: boolean;
  busyId: string | null;
  error: string | null;
  /** Advance one step and/or toggle pass_active. Returns true on success. */
  advance: (
    id: string,
    toStatus: Status,
    passActive?: boolean,
  ) => Promise<boolean>;
}

export function useIntlAccaTournamentAdmin(
  onChanged?: () => void | Promise<void>,
): IntlAccaTournamentAdminVM {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Admin gate via is_strikza_admin() RPC. The original effect treated any
  // failure (RPC error or thrown) as not-admin; useRpcQuery throws on error,
  // so q.data is undefined in that case and (q.data === true) stays false —
  // preserving the exact boolean gate.
  const adminQ = useRpcQuery<boolean>("is_strikza_admin");
  const isAdmin = adminQ.data === true;

  const advance = useCallback(
    async (
      id: string,
      toStatus: Status,
      passActive?: boolean,
    ): Promise<boolean> => {
      setBusyId(id);
      setError(null);
      try {
        const { data, error: rpcErr } = await supabase.rpc(
          "intl_acca_set_tournament_state",
          {
            p_id: id,
            p_status: toStatus,
            p_pass_active: passActive ?? null,
          },
        );
        if (rpcErr) {
          setError(rpcErr.message ?? "Transition failed.");
          return false;
        }
        if (data && data.ok === false) {
          setError(
            data.error === "invalid_transition"
              ? `Invalid transition ${data.from} → ${data.to}.`
              : String(data.error ?? "Transition rejected."),
          );
          return false;
        }
        await onChanged?.();
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Transition failed.");
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [supabase, onChanged],
  );

  return { isAdmin, busyId, error, advance };
}
